/* tslint:disable:no-trailing-whitespace import-spacing no-inferrable-types typedef-whitespace */

import {isset} from '@/util/util';
import {SimConfig} from "@/models/sim/sim.config";

/**
 *
 * @class KPI
 *
 * @brief front side model for a KPI holder; for a given simulation (a scenario that generated
 * train movements)...we have summary metrics, or "key performance indicators".  This class is
 * a holder for all the KPI related to one simulation run.
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      June 2020
 *
 */
export class KPI {

  /** the blob key for the simulation scenario blob */
  public input: string = '';

  /** the blob key for this KPI holder blob */
  public kpi: string = '';

  /** the blob key for the train movement blob */
  public output: string = '';

  /** the label/legend for this KPI holder */
  public label: string = '';

  /** the order (how to sort data series */
  public order: number = 0;

  /** the metrics for this KPI holder */
  public metrics: Map<string, number> = new Map<string, number>();

  /** standard constructor, you cna provide the object we get back from a KPI blob */
  public constructor(options: any) {

    if ( isset(options.input)) {

      this.input = options.input;
    }

    if ( isset(options.output)) {

      this.output = options.output;
    }

    if ( isset(options.key)) {

      this.kpi = options.key;
    }

    if ( isset(options.kpi)) {

      this.kpi = options.kpi;
    }

    if ( isset(options.label)) {

      this.label = options.label;
    }

    if ( isset(options.order)) {

      this.order = options.order;
    }

    if (!isset(options.metrics)) {

      if (options?.sim_results) {

        options.metrics = {sim_results: options.sim_results, corridor_data: options.corridor_data};

      } else {

        return;
      }
    }

    /*
     * for backwards compatibility, support both KPI and KPIX (the extended version of KPI)
     * we dropped our KPI, and now we just use KPIX everywhere.
     *
     */

    let metrics: any  = {};
    let corridor: any = {}

    if (options.metrics?.sim_results) {

      metrics  = options.metrics.sim_results;
      corridor = options.metrics.corridor_data;

    } else {

      metrics  = options.metrics;
    }

    if (Object.keys(corridor).length == 0) {

      /* legacy support for KPI */

      /* fill in whatever metrics we have, if the metrics are known...then label them better */

      for (let [k, v] of Object.entries(metrics)) {

        switch (k.toLowerCase().trim()) {

          case 'averageadditionaldelay':            k = 'Average Additional Delay';                break;
          case 'averagedelayper100miles':           k = 'Average Delay per 100 Miles';             break;
          case 'averagenumberoftrainsperday':       k = 'Average Number of Trains per Day';        break;
          case 'averagescheduleddwell':             k = 'Average Scheduled Dwell';                 break;
          case 'industryspeed':                     k = 'Industry Speed';                          break;
          case 'industryspeedstandarddeviation':    k = 'Industry Speed Standard Deviation';       break;
          case 'networkspeed':                      k = 'Network Speed';                           break;
          case 'networkspeedstandarddeviation':     k = 'Network Speed Standard Deviation';        break;
          case 'numberofmeets':                     k = 'Number of Meets';                         break;
          case 'numberofovertakes':                 k = 'Number of Overtakes';                     break;
          case 'numberofsimulatedtraininkpiperiod': k = 'Number of Simulated Train in KPI Period'; break;
          case 'trainstoppedcount':                 k = 'Train Stopped Count';                     break;
        }

        this.metrics.set(k, +v);
      }

    } else {

      /* support for KPIX (extended version) */

      /* fill in whatever metrics we have, if the metrics are known...then label them better */

      for (let [k, v] of Object.entries(metrics)) {

        switch (k.toLowerCase().trim()) {

          case 'averageadditionaldelay':            k = 'Average Additional Delay';                break;
          case 'averagedelayper100miles':           k = 'Average Delay per 100 Miles';             break;
          case 'averagenumberoftrainsperday':       k = 'Average Number of Trains per Day';        break;
          case 'averagescheduleddwell':             k = 'Average Scheduled Dwell';                 break;
          case 'industryspeed':                     k = 'Industry Speed';                          break;
          case 'industryspeedstandarddeviation':    k = 'Industry Speed Standard Deviation';       break;
          case 'IndustryThroughTrainSpeed':         k = 'Industry Through Train Speed';            break;
          case 'MRT_h':                             k = 'MRT Hours';                               break;
          case 'networkspeed':                      k = 'Network Speed';                           break;
          case 'networkspeedstandarddeviation':     k = 'Network Speed Standard Deviation';        break;
          case 'NetworkThroughTrainSpeed':          k = 'Network Through Train Speed';             break;
          case 'numberofmeets':                     k = 'Number of Meets';                         break;
          case 'numberofovertakes':                 k = 'Number of Overtakes';                     break;
          case 'numberofsimulatedtraininkpiperiod': k = 'Number of Simulated Train in KPI Period'; break;
          case 'trainstoppedcount':                 k = 'Train Stopped Count';                     break;
          case 'conflict_time_h':                   k = 'Conflict Time Hours';                     break;
          case 'sched_dwell_h':                     k = 'Scheduled Dwell Hours';                   break;
          case 'train_miles':                       k = 'Train Miles';                             break;
          case 'train_time':                        k = 'Train Time';                              break;

        }

        this.metrics.set(k, +v);
      }

    }

  }

  /** fetch the value for the given field, if it doesn't exist, return 0. */
  public value(key: string): number {

    if (this.metrics.has(key)) {

      return this.metrics.get(key);
    }

    /* no such key */

    return 0;
  }

  /** fetch the set of field names for metrics in this KPI holder */
  public fields(): string[] {

    return Array.from(this.metrics.keys()) as string[];
  }

  /**
   * all implementations must return a simplified version of themselves, that is appropriate for use
   * with JSON.stringify().
   */
  public toJSON(): any  {

    const obj = Array.from(this.metrics).reduce((obj, [key, value]) => (
      Object.assign(obj, { [key]: value })
    ), {});

    const base = {
      input:   this.input,
      kpi:     this.kpi,
      output:  this.output,
      label:   this.label,
      order:   this.order,
      metrics: obj
    };

    return base;
  }

  /**
   * all implementations must be able to recover themselves from the given object which was
   * deserialized via JSON.parse().
   */
  public fromJSON(json: any) {

    this.input   = json.input;
    this.kpi     = json.kpi;
    this.output  = json.output;
    this.label   = json.label;
    this.order   = json.order;

    const obj    = json.metrics;

    for (let [k, v] of Object.entries(obj.metrics)) {

      this.metrics.set(k, v as number);
    }

  }

}
